import React, { useState, useEffect, useRef } from 'react';
import { Dropdown } from 'react-bootstrap';
import './CommonTable.css';

const CommonTable = ({
  columns = [],
  data = [],
  onRowClick,
  actions = [],
  hideActionsHeader = false,
  emptyMessage = 'No data available',
  emptyStateIcon = 'fas fa-table',
  maxHeight = '85vh',
  minHeight = '530px',
  className = '',
  getRowId = (row, index) => index,
  getRowClassName = null,
  renderCell = null,
}) => {
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const tableWrapperRef = useRef(null);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutsideDropdown = (event) => {
      if (
        openDropdownId &&
        !event.target.closest('.table-actions') &&
        !event.target.closest('.dropdown-menu')
      ) {
        setOpenDropdownId(null);
      }
    };

    if (openDropdownId) {
      document.addEventListener('mousedown', handleClickOutsideDropdown);
      return () => {
        document.removeEventListener('mousedown', handleClickOutsideDropdown);
      };
    }
  }, [openDropdownId]);

  // Check if actions column should be shown
  const hasActions = actions && actions.length > 0;

  // Render empty state
  if (!data || data.length === 0) {
    return (
      <div className="col-12">
        <div className="table-view-container">
          <div className="empty-state">
            <div className="empty-state-icon">
              <i className={emptyStateIcon}></i>
            </div>
            <h3 className="empty-state-title">No Data Found</h3>
            <p className="empty-state-description">{emptyMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  // Determine dropdown direction based on row position
  const getDropdownDirection = (rowIndex, totalRows) => {
    // First 4 rows open downward, last 4 rows open upward
    const bottomThreshold = 4;
    if (rowIndex < 4) {
      return 'down';
    }
    if (rowIndex >= totalRows - bottomThreshold) {
      return 'up';
    }
    return 'down';
  };

  // Scroll to dropdown when opened
  const scrollToDropdown = (rowElement, dropdownDirection) => {
    if (!rowElement || !tableWrapperRef.current) return;

    setTimeout(() => {
      const wrapper = tableWrapperRef.current;
      if (!wrapper) return;

      // Use getBoundingClientRect to get positions relative to viewport
      const wrapperRect = wrapper.getBoundingClientRect();
      const rowRect = rowElement.getBoundingClientRect();
      
      // Calculate positions relative to the wrapper
      const rowTopRelativeToWrapper = rowRect.top - wrapperRect.top + wrapper.scrollTop;
      const rowBottomRelativeToWrapper = rowRect.bottom - wrapperRect.top + wrapper.scrollTop;
      
      const wrapperScrollTop = wrapper.scrollTop;
      const wrapperHeight = wrapper.clientHeight;
      const rowHeight = rowElement.offsetHeight;
      
      // Calculate if dropdown is visible
      const dropdownHeight = 200; // Approximate dropdown menu height
      const isDropup = dropdownDirection === 'up';
      
      if (isDropup) {
        // For dropup, ensure there's space above the row
        const spaceNeeded = dropdownHeight + 20; // 20px padding
        const rowVisibleTop = rowTopRelativeToWrapper - wrapperScrollTop;
        
        if (rowVisibleTop < spaceNeeded) {
          // Scroll up to show the dropdown above the row with smooth animation
          const targetScrollTop = rowTopRelativeToWrapper - spaceNeeded;
          wrapper.scrollTo({
            top: Math.max(0, targetScrollTop),
            behavior: 'smooth'
          });
        }
      } else {
        // For dropdown, ensure there's space below the row
        const spaceNeeded = dropdownHeight + 20; // 20px padding
        const rowVisibleBottom = rowBottomRelativeToWrapper - wrapperScrollTop;
        const spaceBelow = wrapperHeight - rowVisibleBottom;
        
        if (spaceBelow < spaceNeeded) {
          // Scroll down to show the dropdown below the row with smooth animation
          const targetScrollTop = rowBottomRelativeToWrapper + spaceNeeded - wrapperHeight;
          wrapper.scrollTo({
            top: Math.min(wrapper.scrollHeight - wrapperHeight, Math.max(0, targetScrollTop)),
            behavior: 'smooth'
          });
        }
      }
    }, 100); // Delay to ensure dropdown is rendered
  };

  // Render action dropdown
  const renderActions = (row, rowIndex) => {
    if (!hasActions) return null;

    const rowId = getRowId(row, rowIndex);
    const rowActions = typeof actions === 'function' ? actions(row, rowIndex) : actions;

    if (!rowActions || rowActions.length === 0) return null;

    // Determine dropdown direction based on row position
    const dropdownDirection = getDropdownDirection(rowIndex, data.length);

    return (
      <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
        <div className="table-actions">
          <Dropdown
            onClick={(e) => e.stopPropagation()}
            onToggle={(isOpen, event) => {
              if (isOpen) {
                setOpenDropdownId(rowId);
                // Find the row element - try multiple methods
                let rowElement = event?.target?.closest('tr.table-row') || 
                                event?.target?.closest('.table-row') ||
                                event?.currentTarget?.closest('tr.table-row') ||
                                event?.currentTarget?.closest('.table-row');
                
                // If not found, try to find by row index
                if (!rowElement && tableWrapperRef.current) {
                  const rows = tableWrapperRef.current.querySelectorAll('tbody tr.table-row');
                  if (rows[rowIndex]) {
                    rowElement = rows[rowIndex];
                  }
                }
                
                if (rowElement) {
                  scrollToDropdown(rowElement, dropdownDirection);
                }
              } else {
                setOpenDropdownId(null);
              }
            }}
            show={openDropdownId === rowId}
            drop={dropdownDirection}
          >
            <Dropdown.Toggle variant="" as="button" className="dropdown-toggle-btn">
              <i className="fas fa-ellipsis-v"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu align="end">
              {rowActions.map((action, actionIndex) => {
                if (action.hidden && action.hidden(row)) return null;

                return (
                  <Dropdown.Item
                    key={actionIndex}
                    className={action.className || ''}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (action.onClick) {
                        action.onClick(row, rowIndex);
                      }
                      setOpenDropdownId(null);
                    }}
                  >
                    {action.icon && <i className={`${action.icon} me-2`}></i>}
                    {action.label}
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </td>
    );
  };

  // Render cell content
  const renderCellContent = (column, row, rowIndex) => {
    if (renderCell) {
      const customContent = renderCell(column, row, rowIndex);
      if (customContent !== null && customContent !== undefined) {
        return customContent;
      }
    }

    if (column.render) {
      return column.render(row, rowIndex);
    }

    if (column.accessor) {
      const value = typeof column.accessor === 'function'
        ? column.accessor(row, rowIndex)
        : row[column.accessor];

      // If value is a React element (JSX), return it as-is
      // Otherwise convert to string
      if (value !== null && value !== undefined) {
        // Check if it's a React element by checking for $$typeof (React element symbol)
        if (typeof value === 'object' && value.$$typeof) {
          return value;
        }
        return String(value);
      }
      return '';
    }

    return '';
  };

  return (
    <div className="col-12">
      <div className={`table-view-container ${className}`}>
        <div
          className="table-wrapper"
          ref={tableWrapperRef}
          style={{
            maxHeight: maxHeight,
            minHeight: minHeight,
          }}
        >
          <table className="tasks-table">
            <thead style={{ zIndex: 2}}>
              <tr>
                {columns.map((column, index) => (
                  <th
                    key={column.key || index}
                    style={column.headerStyle || {}}
                    className={column.headerClassName || ''}
                  >
                    {column.header || column.label || ''}
                  </th>
                ))}
                {hasActions && (
                  <th className={hideActionsHeader ? 'actions-header-empty' : ''}>
                    {hideActionsHeader ? '' : 'Actions'}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {data.map((row, rowIndex) => (
                <tr
                  key={getRowId(row, rowIndex)}
                  className={`table-row${onRowClick ? ' table-row-clickable' : ''}${getRowClassName ? ` ${getRowClassName(row, rowIndex) || ''}` : ''}`}
                  onClick={(e) => {
                    if (onRowClick) {
                      onRowClick(row, rowIndex, e);
                    }
                  }}
                >
                  {columns.map((column, colIndex) => {
                    const cellContent = renderCellContent(column, row, rowIndex);
                    return (
                      <td
                        key={column.key || colIndex}
                        className={column.cellClassName || ''}
                        style={column.cellStyle || {}}
                      >
                        {cellContent}
                      </td>
                    );
                  })}
                  {renderActions(row, rowIndex)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CommonTable;

